import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { CwqiManager, CwqiSavedEvent, CwqiSavedGuidelinesEvent } from 'app/ext/cwqi/cwqi-manager';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { AuthService } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';
import { getReference } from 'app/shared/models/essette-types';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CWQIService } from 'app/mcg/services/cwqi-service';
import { ApiPostResponse } from 'app/shared/lib/essette-api';
import { localSort } from 'app/shared/lib/sorting';
import { deepClone } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import * as MCG from 'app/mcg/models/essette-api';

@autoinject()
export class SaveCwqi extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    private auth: UM.Auth | null = null;

    @bindable.array
    private guidelines: MCG.CwqiEpisodeGuideline[] = [];

    protected sorting = new Sorting();

    private authCareDays: UM.AdmissionCareDay[] | null = null;
    private careDayChanges: careDayChange[] = [];
    protected displayCareDayChanges: careDayChange[] = [];
    private levelsOfCare: UM.LevelOfCare[] | null = null;
    private dischargeDate: DateValue | null = null;
    private dischargeLocations: UM.DischargeLocation[] | null = null;
    private dischargeLocation: UM.DischargeLocation | undefined = undefined;

    private loadedGuidelines: MCG.CwqiEpisodeGuideline[] = [];


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected cwqi: CwqiManager,
        protected readonly admissionCareDayService: AdmissionCareDayService,
        private readonly authService: AuthService,
        private readonly cwqiService: CWQIService,
        private readonly events: EventAggregator,
        private readonly context: AuthPageContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.loadLookups();
        this.reloadCareDayChanges();
    }

    ////****************** Functions ************************************************////
    protected careDayChangesChanged(): void {
        this.reloadCareDayChanges();
    }

    protected reloadCareDayChanges(): void {
        this.displayCareDayChanges = deepClone(this.careDayChanges);
        if(this.sorting.entries.length > 0) {
            localSort(this.displayCareDayChanges, this.sorting);
        }
    }

    public async loadSummary(summary: string | null): Promise<boolean> {
        let showModal = false;
        if(!this.auth) return showModal;

        this.authCareDays = await this.loadCareDays(this.auth!.id!);
        this.loadedGuidelines = [];

        if(summary) {
            const parser = new DOMParser();
            const summaryXml: Document = parser.parseFromString(summary, "text/xml");
            const episodeXml: Element = summaryXml.getElementsByTagName('Episode')[0];

            if(episodeXml) {

                const dischargeDateStr: string | null = episodeXml.getAttribute('DischargeDateStr');

                if(dischargeDateStr) {
                    const d: string[] = dischargeDateStr.split('/');
                    this.dischargeDate = DateValue.fromParts({
                        year: parseInt(d[2]),
                        month: parseInt(d[0]),
                        date: parseInt(d[1])
                    });

                    const location: string = episodeXml.getAttribute('DischargeDestination')!;
                    this.dischargeLocation = this.dischargeLocations!.find((l) => l.$display == location);
                }

                this.careDayChanges = [];

                for(const element of summaryXml.getElementsByTagName('Guideline')) {
                    const guidelineName: string = element.getAttribute('GuidelineName')!;
                    const glos: string | null = element.getAttribute('Los') || null;
                    const product: string = element.getAttribute('Publication')!;
                    const code: string = element.getAttribute('OrgOtg')!;
                    const version: string = element.getAttribute('Version')!;

                    // Take account of guidelines and if their indications have been met
                    const guideline: MCG.CwqiEpisodeGuideline = {
                        authID: this.auth?.id ? Number(<string>this.auth.id) : null,
                        externalSystemID: 8001,
                        product: product,
                        code: code,
                        name: guidelineName,
                        version: version,
                        lengthOfStay: glos
                    };


                    // Check if guideline status has been met
                    guideline.indicationsGuideline = false;
                    guideline.isIndicationsMet = false;
                    for(const outline of element.getElementsByTagName('Outline')) {
                        const sectionTitles = outline.getElementsByTagName('SectionTitle');
                        if(sectionTitles && sectionTitles.length > 0) {
                            const isClinicalIndication = sectionTitles[0].innerHTML.toLowerCase().indexOf('clinical indication') > -1;

                            if(isClinicalIndication) {
                                guideline.indicationsGuideline = true;
                                const indicationMetStr = outline.getAttribute('IndicationMetStr');

                                if(indicationMetStr) {
                                    guideline.indicationsStatus = indicationMetStr;
                                }

                                if(guideline.indicationsStatus == 'Indications Met') {
                                    guideline.isIndicationsMet = true;
                                    break;
                                }
                            }
                        }
                    }


                    for(const careday of element.getElementsByTagName('CareDay')) {
                        const caredayMetStatusAttr: string | null = careday.getAttribute('Status');
                        const careDayMetStatus: number | null = caredayMetStatusAttr === null ? null : +caredayMetStatusAttr;
                        const careDateStr: string[] = careday.getAttribute('CareDateStr')!.split('/');
                        const careDate: DateValue = DateValue.fromParts({
                            year: parseInt(careDateStr[2]),
                            month: parseInt(careDateStr[0]),
                            date: parseInt(careDateStr[1])
                        });
                        if(!careday.getAttribute('UpdateDate')) {
                            throw new Error('CWQI Summary Xml did not include UpdateDate attribute on care day');
                        }
                        const updateDateStr: string = careday.getAttribute('UpdateDate') + 'Z';
                        const updateDate: DateTimeValue = new DateTimeValue(updateDateStr);
                        const levelOfCare: string = careday.getAttribute('LevelOfCare') || '';

                        let careDayID: string | null = null;
                        let change: string = 'New Care Day';
                        let active: boolean = true;
                        let activeChanged: boolean = false;
                        if(this.authCareDays) {

                            const authCareDay: UM.AdmissionCareDay | undefined = this.authCareDays.find((a) => a.careDate === careDate.toJSON());

                            if(authCareDay && authCareDay.metadata?.modified) { //careday exists in Essette and CWQI
                                careDayID = authCareDay.id!;

                                const lastUpdated: DateTimeValue = new DateTimeValue(authCareDay.guidelineModifiedDateTime || authCareDay.metadata.modified.timestamp!);
                                if(authCareDay.isActive === false) {
                                    change = 'Re-Activate';
                                    activeChanged = true;
                                } else if(updateDate.isAfter(lastUpdated)) {
                                    change = 'Edited';
                                } else {
                                    change = 'No Change';
                                }
                            }
                        }

                        const careDayChange: careDayChange = {
                            careDayID: careDayID,
                            careDate: careDate.toString(),
                            guideline: guidelineName,
                            guidelineStatus: careDayMetStatus,
                            careDayMetStatus: careDayMetStatus,
                            careDayMet: careDayMetStatus === 1 ? 'met' : careDayMetStatus === 0 ? 'not met' : null,
                            glos: glos,
                            product: product,
                            levelOfCare: levelOfCare,
                            updateDate: updateDate.toString(),
                            change: change,
                            active: active,
                            activeChanged: activeChanged,
                            indicationsStatus: guideline.indicationsStatus
                        }
                        this.careDayChanges.push(careDayChange);
                    }


                    this.loadedGuidelines.push(guideline);
                }

                for(const careDay of this.authCareDays) {
                    const careDayChange = this.careDayChanges.find((a) => a.careDate === careDay.careDate);
                    if(!careDayChange && careDay.guideline && careDay.isActive) {  //active careday w/ guideline exists in Essette and not in CWQI
                        const newCareDayChange: careDayChange = {
                            careDayID: careDay.id,
                            careDate: careDay.careDate,
                            guideline: careDay.guideline,
                            levelOfCare: careDay.levelOfCare?.actual?.$display,
                            updateDate: this.env.now(),
                            change: 'De-Activate',
                            active: false,
                            activeChanged: true
                        }
                        this.careDayChanges.push(newCareDayChange);
                    }
                }

                // Save parsed guidelines
                if(this.loadedGuidelines.length || (this.guidelines.length != this.loadedGuidelines.length)) {
                    this.saveGuidelines();
                }

                this.reloadCareDayChanges();

                if(this.careDayChanges.length > 0 || this.dischargeDate) {
                    showModal = true;
                }
            }
        }
        return showModal;
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reloadCareDayChanges();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async save(): Promise<void> {
        if(!this.auth) { throw new Error('No auth found'); }
        try {
            this.state.beginSave();

            for(let c of this.careDayChanges) {

                const LOC: UM.LevelOfCare | undefined = this.levelsOfCare!.find((l) => l.$display == c.levelOfCare);

                if(c.careDayID) {
                    const updatedCareDay: UM.AdmissionCareDay = {
                        id: c.careDayID,
                        careDate: c.careDate,
                        guideline: c.guideline,
                        guidelineModifiedDateTime: c.updateDate,
                        guidelineStatus: c.guidelineStatus,
                        glos: c.glos,
                        product: c.product
                    }

                    if(LOC) { updatedCareDay.levelOfCare = { actual: getReference(LOC) } }

                    if(c.activeChanged) {
                        await this.admissionCareDayService.activateCareDay(updatedCareDay.id!, c.active);
                    }

                    const response = await this.admissionCareDayService.updateCareDay(updatedCareDay);
                    if(!response.isSuccessful) {
                        console.log(response.resultText);
                    }

                } else {
                    const newCareDay: UM.AdmissionCareDay = {
                        auth: { id: this.auth.id },
                        careDate: c.careDate,
                        guideline: c.guideline,
                        guidelineModifiedDateTime: c.updateDate,
                        guidelineStatus: c.guidelineStatus,
                        glos: c.glos,
                        product: c.product
                    }

                    if(LOC) { newCareDay.levelOfCare = { actual: getReference(LOC) } }

                    const response = await this.admissionCareDayService.insertCareDay(newCareDay);
                    if(!response.isSuccessful) {
                        console.log(response.resultText);
                    }
                }

            }

            if(this.dischargeDate) {
                const saveAuth: UM.Auth = {
                    id: this.auth!.id,
                    // must provide MCG episodeID for CWQI Discharge API call
                    externalSystem: {
                        code: 'CWQI',
                        externalResultsIdentifier: this.auth.number ? this.cwqi.getEpisodeID(this.auth.number) : null
                    },
                    discharge: {
                        date: this.dischargeDate.toJSON()
                    }
                };

                if(this.dischargeLocation && saveAuth.discharge) {
                    // additional location display info is needed for CWQI Discharge API call
                    saveAuth.discharge.location = { id: this.dischargeLocation.id, $display: this.dischargeLocation.$display || null }
                }

                const response = await this.authService.saveAuth(saveAuth);
                if(!response.isSuccessful) {
                    console.log(response.resultText);
                }
            }

            this.events.publish(new CwqiSavedEvent());

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            await this.context.refreshAuthSettings();
            this.router.navigateToRoute('um.auth.care-days');
        }
    }

    @apiCall
    protected async saveGuidelines(): Promise<void> {
        const promises = new Array<Promise<ApiPostResponse>>();

        // Insert or update cwqi guidelines
        this.loadedGuidelines.forEach(guideline => {
            promises.push(this.cwqiService.insertCwqiEpisodeGuideline(guideline));
        });

        // Deactivate guidelines deleted from cwqi
        const guidelinesToDelete =
            this.guidelines.filter(guideline => {
                return this.loadedGuidelines.map(g => g.code).indexOf(guideline.code) == -1;
            });

        guidelinesToDelete.forEach(guideline =>
            promises.push(this.cwqiService.activateCwqiEpisodeGuideline(guideline.id!, false))
        );

        let result: boolean = true;
        try {
            this.state.beginSave();
            const responses = await Promise.all(promises);

            responses.forEach((r) => {
                if(!r.isSuccessful) {
                    result = false;
                    console.log(r.resultText);
                }
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            this.events.publish(new CwqiSavedGuidelinesEvent());
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        //we need this because we have to match LOC and Discharge Locations on display because MCG does not know our IDs

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginSave();

            const [responseDischargeLocations, responseLevelsOfCare] = await Promise.all([
                this.admissionCareDayService.loadDischargeLocations(options, undefined, fields),
                this.admissionCareDayService.loadLevelsOfCare(options, undefined, fields)
            ]);

            this.dischargeLocations = responseDischargeLocations.results;
            this.levelsOfCare = responseLevelsOfCare.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCareDays(authId: string): Promise<UM.AdmissionCareDay[]> {
        if(!authId) { return []; }

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: true
        };

        const includes: string[] = ['levelOfCare.actual', 'levelOfCare.approved', 'status', 'status.authStatus', 'status.reasons', 'authMedicalReview', 'auth.externalSystem'];

        try {
            this.state.beginLoad();
            const response = await this.admissionCareDayService.loadCareDays(authId, options, includes);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}

export interface careDayChange {
    careDayID?: string | null,
    careDate?: string | null,
    guideline?: string | null,
    guidelineStatus?: number | null,
    careDayMetStatus?: number | null,
    careDayMet?: string | null,
    glos?: string | null,
    product?: string | null,
    levelOfCare?: string | null,
    updateDate: string,
    change: string | null,
    active: boolean,
    activeChanged: boolean,
    indicationsStatus?: string | null
}
